import React, { useState, useEffect, useMemo, useRef } from "react";
import StatsPreview from "../interfaces/StatsPreview";
import {
  getPreviewMirror,
  isPreviewSyncing,
  subscribeToPreviewMirror,
  subscribeToPreviewSyncState,
} from "@appsinti/preview-mirror";
import { useCurrencySelector } from "@appsinti/currency-provider";

export type DataContextType = {
  loading: boolean;
  preview: StatsPreview | null;
  config: { appId: number };
};

const DataContext = React.createContext<DataContextType>({
  loading: false,
  preview: null,
  config: { appId: 0 },
});

export const usePreviewData = () => React.useContext(DataContext);

const applyCurrency = (
  preview: StatsPreview | null,
  currency: string | null | undefined,
): StatsPreview | null => {
  if (!preview) return null;
  if (!currency) return preview;
  const conv = preview.conversions?.[currency];
  if (!conv) return preview;
  return {
    ...preview,
    total: { ...preview.total, ...conv.total } as typeof preview.total,
    lunch: (conv.lunch ?? preview.lunch) as typeof preview.lunch,
    dinner: (conv.dinner ?? preview.dinner) as typeof preview.dinner,
  };
};

export const PreviewDataProvider: React.FC<{
  appId: number;
  children: any;
}> = ({ appId, children }) => {
  const [rawPreview, setRawPreview] = useState<StatsPreview | null>(null);
  const [syncing, setSyncing] = useState<boolean>(() => isPreviewSyncing(appId));
  const [settled, setSettled] = useState<boolean>(false);
  const wasSyncing = useRef<boolean>(false);
  const { currency } = useCurrencySelector();

  useEffect(() => {
    if (!appId) return;

    setSettled(false);
    setSyncing(isPreviewSyncing(appId));
    wasSyncing.current = isPreviewSyncing(appId);

    const readLocalPreview = () => {
      const mirror = getPreviewMirror(appId);
      if (!mirror) return;
      try {
        setRawPreview(JSON.parse(mirror) as StatsPreview);
      } catch {
        return;
      }
    };

    const readSyncState = () => {
      const isSyncing = isPreviewSyncing(appId);
      // Settled only on this app's own transition out of syncing: other apps
      // starting or finishing must not resolve this card.
      if (wasSyncing.current && !isSyncing) setSettled(true);
      wasSyncing.current = isSyncing;
      setSyncing(isSyncing);
    };

    readLocalPreview();

    const unsubscribeMirror = subscribeToPreviewMirror(readLocalPreview);
    const unsubscribeSyncState = subscribeToPreviewSyncState(readSyncState);

    return () => {
      unsubscribeMirror();
      unsubscribeSyncState();
    };
  }, [appId]);

  const loading = syncing || !settled;

  const preview = useMemo(
    () => applyCurrency(rawPreview, currency),
    [rawPreview, currency],
  );

  return (
    <DataContext.Provider value={{ loading, preview, config: { appId } }}>
      {children}
    </DataContext.Provider>
  );
};
